import { h, Fragment } from 'preact';
import { route } from 'preact-router';
import { memo } from 'preact/compat';
import { useState, useEffect, useRef } from 'preact/hooks';
import isEqual from 'lodash.isequal';

import { getCatalogueTheme } from '../../lib/Theme';
import {
  getColorContrastBetweenRgb,
  hexToRGBValues,
  isDesktopView,
  shouldProcessClick,
  isNavigatingToCurrentLink
} from '../../lib/Utils';
import { DEVICE_TYPE, DISPLAY_TYPE, LINK_TYPE } from '../../pages/CustomPageBanner/helpers';

import './styles.less';

const CATEGORIES_SECTION_TITLE_LENGTH_MULTIPLIER = 6.25;
const CATEGORIES_SECTION_ITEM_MIN_LENGTH = 96;
const CATEGORIES_SECTION_ITEM_MAX_LENGTH = 125;

export const CustomPageCategoriesList = ({ sectionId, categories = [] }) => {
  const isDesktop = isDesktopView();
  const categoriesCount = categories.length;
  const categoriesSectionListRef = useRef(null);
  const [categoriesSectionsRows, setCategoriesSectionsRows] = useState([
    { start: 0, end: categoriesCount }
  ]);
  const [categoriesSectionsRowsCalculated, setCategoriesSectionsRowsCalculated] = useState(false);

  useEffect(() => {
    if (isDesktop && categoriesSectionListRef.current) {
      const windowWidth = window.innerWidth;
      let accumulatedWidth = 0;
      let columnCount = 0;
      const categoriesSectionListItemElements = categoriesSectionListRef.current.querySelectorAll(
        '.categoriesSectionListItem'
      );
      categoriesSectionListItemElements.forEach((categoriesSectionListItemElement, index) => {
        let width = CATEGORIES_SECTION_ITEM_MIN_LENGTH;
        if (categories[index].title) {
          width = Math.min(
            categories[index].title.trim().length * CATEGORIES_SECTION_TITLE_LENGTH_MULTIPLIER,
            CATEGORIES_SECTION_ITEM_MAX_LENGTH
          );
        }
        categoriesSectionListItemElement.style.width = `${width}`;
        categoriesSectionListItemElement.style.minWidth = `${width}`;
        accumulatedWidth += width;
        if (accumulatedWidth > windowWidth - 20 && columnCount === 0) {
          accumulatedWidth = 0;
          columnCount = index - 2;
        }
        if (index === categoriesCount - 1 && columnCount === 0) {
          columnCount = index + 1;
        }
      });
      let rows = [...categoriesSectionsRows];
      // If the categories are more than 2 rows then distribute equally
      if (categoriesCount > 2 * columnCount) {
        const middle = categoriesCount / 2;
        rows = [
          { start: 0, end: Math.floor(middle) + 1 },
          { start: Math.floor(middle) + 1, end: categoriesCount }
        ];
      }
      if (categoriesCount > columnCount && categoriesCount <= 2 * columnCount) {
        rows = [
          { start: 0, end: columnCount },
          { start: columnCount, end: categoriesCount }
        ];
      }
      if (!categoriesSectionsRowsCalculated) {
        setCategoriesSectionsRows(rows);
        setCategoriesSectionsRowsCalculated(true);
      }
    }
  }, [categoriesSectionListRef.current]);

  const { pageTextColor = '#000' } = getCatalogueTheme() || {};
  const { backgroundColor = '#fff' } = getCatalogueTheme().homeGrid || {};
  const { backgroundColor: primaryColor = '#000', textColor: backgroundTextColor = '#000' } =
    getCatalogueTheme().addToCartButton;
  const backgroundTextColorContrast = getColorContrastBetweenRgb({
    rgb1: hexToRGBValues(backgroundTextColor),
    rgb2: hexToRGBValues(backgroundColor)
  });
  const primaryColorContrast = getColorContrastBetweenRgb({
    rgb1: hexToRGBValues(primaryColor),
    rgb2: hexToRGBValues(backgroundColor)
  });
  const categoriesSectionTitleHoverColor =
    primaryColorContrast > backgroundTextColorContrast ? primaryColor : backgroundTextColor;

  const [useHoverColorForCategoriesSectionId, setUseHoverColorForCategoriesSectionId] =
    useState(null);

  const handleLinkClick = (event, banner) => {
    if (
      !banner.linkType ||
      !banner.linkAction ||
      banner.linkType === LINK_TYPE.EXTERNAL_LINK ||
      banner.linkType === LINK_TYPE.PAGE_COMPANY_DETAILS
    ) {
      return;
    }

    if (!shouldProcessClick(event)) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (isNavigatingToCurrentLink(banner.linkAction)) {
      return;
    }

    route(banner.linkAction);
  };

  const getCurrentBannerDisplay = (banner) => {
    if (banner && banner.bannerDisplay && banner.bannerDisplay.length > 0) {
      const desktopBannerDisplay = banner.bannerDisplay.find(
        (display) => display.deviceType === DEVICE_TYPE.DESKTOP
      );
      const mobileBannerDisplay = banner.bannerDisplay.find(
        (display) => display.deviceType === DEVICE_TYPE.MOBILE
      );
      const currentBannerDisplay = isDesktop
        ? desktopBannerDisplay || mobileBannerDisplay
        : mobileBannerDisplay || desktopBannerDisplay;
      return currentBannerDisplay || {};
    }
    return banner || {};
  };

  const renderCategorySectionContent = ({ categoriesSectionId, banner, title, index }) => {
    const currentBannerDisplay = getCurrentBannerDisplay(banner);
    if (
      !currentBannerDisplay ||
      !currentBannerDisplay.displayUrl ||
      !currentBannerDisplay.prepared ||
      currentBannerDisplay.displayType !== DISPLAY_TYPE.IMAGE
    ) {
      return null;
    }
    return (
      <Fragment>
        <img
          src={`${currentBannerDisplay.displayUrl}?v=${currentBannerDisplay.displayVersion || 0}`}
          alt={title}
          height={currentBannerDisplay.displayHeight}
          width={currentBannerDisplay.displayWidth}
          className="categoriesSectionImage"
          draggable={false}
          itemProp="contentUrl"
          loading={index === 0 ? 'eager' : 'lazy'}
          fetchPriority="low"
        />
        {title && (
          <div
            className="categoriesSectionTitle"
            style={{
              color:
                useHoverColorForCategoriesSectionId === categoriesSectionId
                  ? categoriesSectionTitleHoverColor
                  : pageTextColor
            }}
          >
            {title}
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <section className="sectionContainer categoriesSectionContainer" key={sectionId}>
      {categoriesSectionsRows.map(({ start, end }, index) => (
        <div className="categoriesSectionListContainer" key={index}>
          <div className="categoriesSectionList" ref={categoriesSectionListRef}>
            {categories
              .slice(start, end)
              .map(({ banner, title, id: categoriesSectionId }, index) => {
                if (!banner || !banner.bannerDisplay) {
                  return null;
                }
                const categoriesSectionItemHasLink = !!banner.linkAction && !!banner.linkType;
                const CategoriesSectionItemTagName = categoriesSectionItemHasLink ? 'a' : 'div';
                const categoriesSectionItemTagProps = categoriesSectionItemHasLink
                  ? {
                    href: banner.linkAction,
                    rel:
                        banner.linkType === LINK_TYPE.EXTERNAL_LINK
                          ? 'noopener norererrer nofollow'
                          : undefined,
                    target:
                        banner.linkType === LINK_TYPE.EXTERNAL_LINK ||
                        banner.linkType === LINK_TYPE.PAGE_COMPANY_DETAILS
                          ? '_blank'
                          : '_self',
                    draggable: false,
                    title,
                    onClick: (event) => handleLinkClick(event, banner)
                  }
                  : {};
                return (
                  <CategoriesSectionItemTagName
                    key={index}
                    className={`categoriesSectionListItem${
                      categoriesSectionItemHasLink ? ' categoriesSectionListItemLink' : ''
                    }`}
                    onMouseEnter={() => setUseHoverColorForCategoriesSectionId(categoriesSectionId)}
                    onMouseLeave={() => setUseHoverColorForCategoriesSectionId(null)}
                    {...categoriesSectionItemTagProps}
                    itemType="https://schema.org/ImageObject"
                    itemScope
                  >
                    {renderCategorySectionContent({ categoriesSectionId, banner, title, index })}
                  </CategoriesSectionItemTagName>
                );
              })}
          </div>
        </div>
      ))}
    </section>
  );
};

export default memo(
  CustomPageCategoriesList,
  (prevProps, nextProps) =>
    prevProps.title === nextProps.title && isEqual(prevProps.categories, nextProps.categories)
);
